import React, { useState } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Button, Input, Modal, SelectAsync } from '@arnsbogroup/react-voicearchive'
import debounce from 'debounce-promise'
import { Icon } from '@arnsbogroup/react-voicearchive'

const loadLanguageOptions = debounce((query) =>
  fetch(`/rest/v2/language?search[name]=${encodeURIComponent(query)}&pageSize=50`)
    .then(r => r.json())
    .then(res => {
    const list = Array.isArray(res) ? res : (res.data || [])
      return list
      .filter(l => l.l18n_iso === 'en')
      .map(l => ({ value: String(l.uid), label: l.name }))
    })
, 300)

export default NiceModal.create(({ order, contextType, contextUid, version = null }) => {
  const modal = useModal()
  const [texts, setTexts] = useState([''])
  const [language, setLanguage] = useState(null)
  const [saving, setSaving] = useState(false)

  const isVersionOrTask = contextType === 'version' || contextType === 'task'
  const autoLanguageUid = isVersionOrTask
    ? (version?.targetlanguageId || version?.targetlanguage || null)
    : null

  const buildLinks = () => {
    const links = [
      { linkType: 'customer', linkUid: order.customer.uid },
      { linkType: 'order', linkUid: order.uid },
    ]
    if (contextType === 'job' || contextType === 'version' || contextType === 'task') {
      links.push({ linkType: 'ordertask', linkUid: contextUid })
    }
    return links
  }

  const addText = () => setTexts(prev => [...prev, ''])

  const updateText = (i, value) => setTexts(prev => prev.map((t, idx) => idx === i ? value : t))

  const removeText = (i) => setTexts(prev => prev.filter((_, idx) => idx !== i))

  const handleSubmit = async () => {
    const validTexts = texts.map(t => t.trim()).filter(Boolean)
    if (!validTexts.length) return
    setSaving(true)
    try {
      const languageUid = autoLanguageUid
        ? Number(autoLanguageUid)
        : (language?.value ? Number(language.value) : null)
      const links = buildLinks()
      const results = []
      for (const text of validTexts) {
        const { success, data, errors } = await fetch('/rest/v2/memory', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, languageUid, links }),
        }).then(r => r.json())
        if (!success) {
          toastr.error((errors || []).join(', '))
          return
        }
        results.push(data)
      }
      toastr.success(validTexts.length > 1 ? `${validTexts.length} memories created.` : 'Memory created.')
      modal.resolve(results.length === 1 ? results[0] : results)
      modal.remove()
    } catch (e) {
      toastr.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      show={modal.visible}
      title="Create memory"
      onClose={() => modal.remove()}
      dialogPanelClasses="max-w-md"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          {texts.map((text, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                className="flex-1"
                label={i === 0 ? 'Memory text' : undefined}
                value={text}
                onChange={({ target }) => updateText(i, target.value)}
                placeholder="Enter the word or phrase..."
                autoFocus={i === 0}
              />
              {texts.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeText(i)}
                  className="mt-1 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600 shrink-0"
                >
                  <Icon icon={['fas', 'times']} />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addText}
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            <Icon icon={['fas', 'plus']} />
            Add another
          </button>
        </div>

        {!isVersionOrTask && (
          <SelectAsync
            label="Language (optional)"
            value={language}
            onChange={setLanguage}
            loadOptions={loadLanguageOptions}
            defaultOptions
            cacheOptions
            placeholder="Select language..."
            isClearable
            menuPortalTarget={document.body}
          />
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleSubmit}
            disabled={!texts.some(t => t.trim()) || saving}
          >
            {saving ? 'Creating...' : texts.filter(t => t.trim()).length > 1 ? `Create ${texts.filter(t => t.trim()).length} memories` : 'Create memory'}
          </Button>
          <Button variant="outline" onClick={() => modal.remove()}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  )
})
